// frontend/src/screens/admin/sps/SpsDashboardScreen.jsx
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useGetPayDataSummaryQuery }  from '../../../slices/spsApiSlices';
import { useGetCpiRatesQuery }        from '../../../slices/spsApiSlices';
import { useGetRunHistoryQuery }      from '../../../slices/spsApiSlices';
import { useGetEmailLogQuery }        from '../../../slices/spsApiSlices';
import { useGetStaffDirectoryQuery }  from '../../../slices/spsApiSlices';

// ── Small stat card ───────────────────────────────────────────────────────────
function StatCard({ label, value, sub, colour = 'primary', link, linkLabel }) {
  return (
    <Card className={`border-${colour} h-100`}>
      <Card.Body className="d-flex flex-column">
        <div className="text-muted small mb-1">{label}</div>
        <div className={`fw-bold text-${colour}`} style={{ fontSize: '1.6rem', lineHeight: 1.1 }}>
          {value ?? <Spinner animation="border" size="sm" />}
        </div>
        {sub && <div className="text-muted" style={{ fontSize: '.75rem', marginTop: 2 }}>{sub}</div>}
        {link && (
          <div className="mt-auto pt-2">
            <LinkContainer to={link}>
              <Button size="sm" variant={`outline-${colour}`}>{linkLabel || 'View →'}</Button>
            </LinkContainer>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Recent run row ────────────────────────────────────────────────────────────
function RunRow({ run }) {
  const dur = run.completedAt && run.startedAt
    ? Math.round((new Date(run.completedAt) - new Date(run.startedAt)) / 1000)
    : null;
  return (
    <div className="d-flex align-items-center gap-2 py-2 border-bottom" style={{ fontSize: '.82rem' }}>
      <Badge bg={run.runType === 'batch' ? 'dark' : 'secondary'} style={{ fontSize: '.62rem', width: 60, textAlign: 'center' }}>
        {run.runType}
      </Badge>
      <div className="flex-grow-1">
        <span className="fw-semibold">
          {run.runType === 'individual' ? `${run.staffName} (${run.ppsNumber})` : 'All staff'}
        </span>
        <span className="text-muted ms-2">{run.runYear}</span>
      </div>
      <Badge bg={run.status === 'completed' ? 'success' : run.status === 'error' ? 'danger' : 'warning'}>
        {run.status}
      </Badge>
      {dur !== null && <span className="text-muted" style={{ fontSize: '.72rem', minWidth: 32 }}>{dur}s</span>}
      <span className="text-muted" style={{ fontSize: '.72rem' }}>
        {new Date(run.startedAt).toLocaleDateString('en-IE')}
      </span>
    </div>
  );
}

export default function SpsDashboardScreen() {
  const { data: paySummary }     = useGetPayDataSummaryQuery();
  const { data: cpiRates = [] }  = useGetCpiRatesQuery();
  const { data: runHistory }     = useGetRunHistoryQuery({ page: 1 });
  const { data: emailLog }       = useGetEmailLogQuery({ page: 1 });
  const { data: staffData }      = useGetStaffDirectoryQuery({ page: 1, limit: 1 });

  const recentRuns   = runHistory?.runs   || [];
  const recentEmails = emailLog?.logs     || [];
  const lastBatch    = recentRuns.find((r) => r.runType === 'batch');
  const pendingCpi   = cpiRates.filter((r) => r.isPending);

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-4">
        <Col>
          <h4 className="mb-0">SPS Pension — Dashboard</h4>
          <small className="text-muted">Single Public Service Pension Scheme administration</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <LinkContainer to="/admin/sps/run">
            <Button variant="success" size="sm">▶ Run Calculator</Button>
          </LinkContainer>
          <LinkContainer to="/admin/sps/import">
            <Button variant="outline-primary" size="sm">⬆ Import Data</Button>
          </LinkContainer>
        </Col>
      </Row>

      {/* Alerts */}
      {pendingCpi.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <strong>⚠ Pending CPI rates:</strong> {pendingCpi.map((r) => r.year).join(', ')} — these are
          placeholders and have not been officially confirmed by DPER.{' '}
          <LinkContainer to="/admin/sps/cpi-rates">
            <Alert.Link>Update CPI rates →</Alert.Link>
          </LinkContainer>
        </Alert>
      )}

      {lastBatch?.status === 'error' && (
        <Alert variant="danger" className="mb-3">
          <strong>Last batch run failed</strong> on {new Date(lastBatch.startedAt).toLocaleDateString('en-IE')}.{' '}
          <LinkContainer to="/admin/sps/run-history">
            <Alert.Link>View run log →</Alert.Link>
          </LinkContainer>
        </Alert>
      )}

      {/* Key stats */}
      <Row className="g-3 mb-4">
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="Total Staff"
            value={staffData?.total != null ? staffData.total.toLocaleString() : null}
            colour="primary"
            link="/admin/sps/staff"
            linkLabel="Directory →"
          />
        </Col>
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="Pay Records"
            value={paySummary?.totalRecords != null ? paySummary.totalRecords.toLocaleString() : null}
            sub={paySummary ? `${paySummary.minYear} – ${paySummary.maxYear}` : ''}
            colour="secondary"
            link="/admin/sps/pay-records"
            linkLabel="Browse →"
          />
        </Col>
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="CPI Rates Loaded"
            value={cpiRates.length || null}
            sub={pendingCpi.length > 0 ? `${pendingCpi.length} pending` : 'All confirmed'}
            colour={pendingCpi.length > 0 ? 'warning' : 'success'}
            link="/admin/sps/cpi-rates"
            linkLabel="Edit →"
          />
        </Col>
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="Last Batch Run"
            value={lastBatch ? lastBatch.runYear : '—'}
            sub={lastBatch ? new Date(lastBatch.startedAt).toLocaleDateString('en-IE') : 'Not yet run'}
            colour={!lastBatch ? 'secondary' : lastBatch.status === 'completed' ? 'success' : 'danger'}
            link="/admin/sps/run-history"
            linkLabel="History →"
          />
        </Col>
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="Emails Sent"
            value={emailLog?.total != null ? emailLog.total.toLocaleString() : null}
            colour="info"
            link="/admin/sps/email-log"
            linkLabel="Log →"
          />
        </Col>
        <Col xs={6} sm={4} lg={2}>
          <StatCard
            label="Last Run Status"
            value={recentRuns[0] ? (
              <Badge bg={recentRuns[0].status === 'completed' ? 'success' : recentRuns[0].status === 'error' ? 'danger' : 'warning'} style={{ fontSize: '.9rem' }}>
                {recentRuns[0].status}
              </Badge>
            ) : '—'}
            colour="secondary"
          />
        </Col>
      </Row>

      {/* Quick actions */}
      <Row className="g-3 mb-4">
        {[
          { title: 'CPI Rates',      desc: 'View and edit annual CPI uplift rates from DPER circulars',    link: '/admin/sps/cpi-rates',   colour: 'warning' },
          { title: 'CSP Assumptions', desc: 'Edit Contributory State Pension rate parameters by year',       link: '/admin/sps/csp-rates',   colour: 'info' },
          { title: 'Import Pay Data', desc: 'Upload the SPSPS XLSM file to refresh pay records in DB', link: '/admin/sps/import',      colour: 'primary' },
          { title: 'Staff Directory', desc: 'View and edit member contact details and ABS Part A fields',   link: '/admin/sps/staff',       colour: 'success' },
          { title: 'Run Calculator',  desc: 'Generate certificates for all staff or one individual',         link: '/admin/sps/run',         colour: 'success' },
          { title: 'Email Statements',desc: 'Send Annual Benefit Statements to staff by email',             link: '/admin/sps/email',       colour: 'primary' },
        ].map(({ title, desc, link, colour }) => (
          <Col xs={12} sm={6} lg={4} key={title}>
            <LinkContainer to={link}>
              <Card className={`h-100 border-${colour} border-2 cursor-pointer`} style={{ cursor: 'pointer' }}>
                <Card.Body>
                  <div className={`fw-bold text-${colour} mb-1`}>{title}</div>
                  <div className="text-muted small">{desc}</div>
                </Card.Body>
              </Card>
            </LinkContainer>
          </Col>
        ))}
      </Row>

      {/* Recent activity */}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="fw-semibold bg-light d-flex justify-content-between">
              <span>Recent Calculator Runs</span>
              <LinkContainer to="/admin/sps/run-history">
                <Button size="sm" variant="link" className="p-0">View all</Button>
              </LinkContainer>
            </Card.Header>
            <Card.Body className="p-0 px-3">
              {recentRuns.length === 0 ? (
                <p className="text-muted small py-3">No runs yet.</p>
              ) : (
                recentRuns.slice(0, 8).map((r) => <RunRow key={r._id} run={r} />)
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="fw-semibold bg-light d-flex justify-content-between">
              <span>Recent Emails</span>
              <LinkContainer to="/admin/sps/email-log">
                <Button size="sm" variant="link" className="p-0">View all</Button>
              </LinkContainer>
            </Card.Header>
            <Card.Body className="p-0 px-3">
              {recentEmails.length === 0 ? (
                <p className="text-muted small py-3">No emails sent yet.</p>
              ) : (
                recentEmails.slice(0, 8).map((e) => (
                  <div key={e._id} className="d-flex align-items-center gap-2 py-2 border-bottom" style={{ fontSize: '.82rem' }}>
                    <div className="flex-grow-1">
                      <span className="fw-semibold">{e.staffName}</span>
                      <span className="text-muted ms-1" style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>({e.ppsNumber})</span>
                    </div>
                    <span className="text-muted" style={{ fontSize: '.72rem' }}>{e.statementYear}</span>
                    <Badge bg={e.status === 'sent' ? 'success' : 'danger'} style={{ fontSize: '.62rem' }}>
                      {e.status}
                    </Badge>
                    <span className="text-muted" style={{ fontSize: '.7rem' }}>
                      {new Date(e.sentAt).toLocaleDateString('en-IE')}
                    </span>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}